import { isRadiantThemeEnabled } from '@thoughtspot/session-service';
/**
 * Returns true when Radiant dark mode is active:
 *  - radiantThemeEnabled session flag must be on
 *  - data-theme="dark" must be set on <html>
 */
export const isDarkModeActive = () => {
    if (!isRadiantThemeEnabled())
        return false;
    return (typeof document !== 'undefined' &&
        document.documentElement.getAttribute('data-theme') === 'dark');
};
// ---------------------------------------------------------------------------
// OKLCH lightness remap
// ---------------------------------------------------------------------------
//
// OKLCH is the cylindrical form of the OKLab perceptual color space:
//   L = perceptual lightness (0..1), C = chroma, H = hue (radians here).
// Remapping lightness in OKLab keeps perceived brightness uniform across hues
// (unlike HSL, where the same L looks brighter for yellow than for blue), so
// palette entries land at a consistent, readable brightness on a dark surface
// while hue and chroma are preserved for inter-slice distinctiveness.
const srgbToLinear = (c) => c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
const linearToSrgb = (c) => c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055;
function hexToOklch(hex) {
    // Reject anything that isn't a full 6-digit "#RRGGBB" hex (shorthand
    // "#F00", 8-digit "#RRGGBBAA", non-hex chars). Without this, slicing a
    // short string yields NaN channels that propagate to "#NaNNaNNaN"; the
    // caller's try/catch turns this throw into a clean passthrough instead.
    if (!/^#[0-9a-fA-F]{6}$/.test(hex)) {
        throw new Error(`hexToOklch: expected a 6-digit "#RRGGBB" hex, got "${hex}"`);
    }
    const r = srgbToLinear(parseInt(hex.slice(1, 3), 16) / 255);
    const g = srgbToLinear(parseInt(hex.slice(3, 5), 16) / 255);
    const b = srgbToLinear(parseInt(hex.slice(5, 7), 16) / 255);
    // linear sRGB -> LMS
    const lPrime = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
    const mPrime = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
    const sPrime = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
    // LMS -> OKLab
    const L = 0.2104542553 * lPrime + 0.793617785 * mPrime - 0.0040720468 * sPrime;
    const a = 1.9779984951 * lPrime - 2.428592205 * mPrime + 0.4505937099 * sPrime;
    const bb = 0.0259040371 * lPrime + 0.7827717662 * mPrime - 0.808675766 * sPrime;
    // OKLab -> OKLCH
    const C = Math.sqrt(a * a + bb * bb);
    const H = Math.atan2(bb, a);
    return [L, C, H];
}
function oklchToHex(L, C, H) {
    // OKLCH -> OKLab
    const a = C * Math.cos(H);
    const bb = C * Math.sin(H);
    // OKLab -> LMS
    const lPrime = L + 0.3963377774 * a + 0.2158037573 * bb;
    const mPrime = L - 0.1055613458 * a - 0.0638541728 * bb;
    const sPrime = L - 0.0894841775 * a - 1.291485548 * bb;
    const l = lPrime * lPrime * lPrime;
    const m = mPrime * mPrime * mPrime;
    const s = sPrime * sPrime * sPrime;
    // LMS -> linear sRGB
    const r = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
    const g = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
    const b = -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s;
    // gamma encode + clamp to sRGB gamut. A simple per-channel clamp is enough
    // here; if out-of-gamut colors look desaturated we can add a
    // chroma-reduction fallback (binary-search C down until in gamut) later.
    const toByte = (c) => Math.round(Math.min(1, Math.max(0, linearToSrgb(c))) * 255)
        .toString(16)
        .padStart(2, '0');
    return `#${toByte(r)}${toByte(g)}${toByte(b)}`;
}
/**
 * Picks the target OKLCH lightness for a data-viz color on a dark surface.
 * Band-based remap in perceptual (OKLab) lightness 0..1:
 *  - Very dark colors (L < 0.30): lift to 0.78 — big, vibrant lift
 *  - Mid-range colors (L < 0.65): lift to 0.80
 *  - Light colors     (L >= 0.65): 0.82 — minimal adjustment
 */
function oklchDarkModeLightness(L) {
    if (L < 0.3)
        return 0.78;
    if (L < 0.65)
        return 0.8;
    return 0.82;
}
/**
 * Shifts each color to a dark-mode-friendly value by remapping its OKLCH
 * lightness into a dark-mode band while preserving chroma and hue, so palette
 * entries stay vivid and distinct on a dark background. Handles "#RRGGBB" hex
 * strings and "rgba(r,g,b,a)" strings (produced by convertToAlpha for bubble
 * and scatter charts); invalid values pass through unchanged.
 */
export const generateDarkModePalette = (lightColors) => lightColors.map((colorStr) => {
    try {
        if (colorStr.startsWith('#')) {
            const [L, C, H] = hexToOklch(colorStr);
            return oklchToHex(oklchDarkModeLightness(L), C, H);
        }
        // Handle rgba()/rgb() strings produced by convertToAlpha for
        // bubble and scatter charts. A single bounded capture group keeps
        // the pattern free of nested quantifiers (no ReDoS risk); the
        // comma-split below validates the channel count.
        const rgbMatch = colorStr.match(/^rgba?\(([^)]+)\)$/);
        const parts = rgbMatch ? rgbMatch[1].split(',') : [];
        if (parts.length === 3 || parts.length === 4) {
            const r = parseInt(parts[0], 10);
            const g = parseInt(parts[1], 10);
            const b = parseInt(parts[2], 10);
            const alpha = parts[3] !== undefined ? parseFloat(parts[3]) : 1;
            const toByte = (x) => x.toString(16).padStart(2, '0');
            const hex = `#${toByte(r)}${toByte(g)}${toByte(b)}`;
            const [L, C, H] = hexToOklch(hex);
            const darkHex = oklchToHex(oklchDarkModeLightness(L), C, H);
            const dr = parseInt(darkHex.slice(1, 3), 16);
            const dg = parseInt(darkHex.slice(3, 5), 16);
            const db = parseInt(darkHex.slice(5, 7), 16);
            return `rgba(${dr}, ${dg}, ${db}, ${alpha})`;
        }
        return colorStr;
    }
    catch (_a) {
        // Invalid/unsupported color (shorthand hex, out-of-range rgb,
        // etc.) — pass through unchanged rather than emit "#NaNNaNNaN".
        return colorStr;
    }
});
/**
 * Watches the data-theme attribute on <html> and calls callback whenever it
 * changes. Returns a cleanup function.
 *
 * Used by chart renderers to reactively update series colors when the user
 * toggles between light and dark mode while a chart is already on screen, and
 * to handle the timing gap between chart mount and the useEffect in
 * authenticated-app-layout that sets data-theme.
 */
export const subscribeToThemeChanges = (callback) => {
    if (typeof document === 'undefined') {
        return () => undefined;
    }
    const observer = new MutationObserver(callback);
    observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['data-theme'],
    });
    return () => {
        observer.disconnect();
    };
};
//# sourceMappingURL=dark-mode-color-utils.js.map